import React, { useState } from 'react';
import Divider from '@material-ui/core/Divider';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';

import { makeStyles } from '@material-ui/core/styles';
import Alert from '../Alert';
import { useUser } from '../../context/UserContext';
import NavBar from '../NavBar';
import { Page, Form, LocalTheme } from 'ecrion-components';
import DocumentTitle from 'react-document-title';
import { changePassword } from '../../utils/userPropertiesClient';
import { getContactsForm } from '../../utils/contentClient';
import PageNotFound from '../PageNotFound';
import FullPageProgress from '../FullPageProgress';
import moment from 'moment';
import { getPasswordPolicies } from '../../utils/portalPasswordsClient';

const useStyles = makeStyles({
  clearDivider: {
    opacity: 0,
  },
  alertInfo: {
    textAlign: 'center',
    width: '100%',
  },
  changePassBtn: {
    backgroundColor: '#FF6633',
    color: '#ffffff',
    minWidth: '80px',
    borderRadius: '6px',
    border: 0,
    boxShadow:
      'rgba(0, 0, 0, 0.2) 0px 1px 3px 0px, rgba(0, 0, 0, 0.14) 0px 1px 1px 0px, rgba(0, 0, 0, 0.12) 0px 2px 1px -1px;',
    '&:hover': {
      backgroundColor: '#FF9933',
    },
  },
});

const customTheme = {
  overrides: {
    MuiButton: {
      root: {
        borderRadius: '6px',
        backgroundColor: '#FF6633',
        color: 'white',
        '&:focus': {
          backgroundColor: '#FF6633',
          color: 'white',
        },
        '&:hover': {
          backgroundColor: '#FF9933',
          color: 'white',
        },
        '&.Mui-disabled': {
          color: 'white !important',
        },
      },
    },
  },
};

const MyAccount = ({ match, history }) => {
  const [passwordDialog, setPasswordDialog] = useState(false);
  const [contactProperties, setContactProperties] = useState(null);
  const [error, setError] = useState(null);
  const user = useUser();
  const receivedEnvironment = decodeURIComponent(match.params.environment);
  if (receivedEnvironment !== user.Environment) {
    return <PageNotFound history={history} />;
  }

  const classes = useStyles();

  const handlePasswordChange = () => {
    setPasswordDialog(true);
  };

  const loadData = () => {
    const getPropertyValue = (propertyName) => {
      const contactProp = user.Contact.Properties.find(
        (el) => el.Name === propertyName
      );
      if (!contactProp) return '';

      if (contactProp.Type === 4 && contactProp.Value) {
        return moment(contactProp.Value).format('MMM D YYYY');
      }
      return contactProp.Value ? contactProp.Value : '';
    };

    getContactsForm()
      .then((xmlDoc) => {
        const categorizedProperties = Array.from(
          xmlDoc.getElementsByTagName('page')
        )
          .filter((page) => {
            const pageName = page.attributes.getNamedItem('name').value;
            return pageName !== 'Sales';
          })
          .map((page) => {
            const pageName = page.attributes.getNamedItem('name').value;
            const categoryObj = [];
            const childNodes = page.childNodes;
            for (let i = 0; i < childNodes.length; i++) {
              const attributes = childNodes[i].attributes;
              if (!attributes) continue;

              const propertyName = attributes.getNamedItem('name').value;
              const propertyLabel = attributes.getNamedItem('caption').value;

              const propertyValue = getPropertyValue(propertyName);
              categoryObj.push({
                name: propertyName,
                label: propertyLabel,
                value: propertyValue,
              });
            }
            return { categoryName: pageName, properties: categoryObj };
          });
        setContactProperties(categorizedProperties);
      })
      .catch((e) => {
        setError(
          'An error has occurred while loading the profile details. Error: ' +
            e.message
        );
      });
  };

  const [passwordPolicies, setPasswordPolicies] = useState(null);

  React.useEffect(() => {
    getPasswordPolicies().then((policies) => {
      setPasswordPolicies(policies);
    });
  }, []);

  function validatePassword(password, passwordConfirmation) {
    if (passwordPolicies['PasswordLength'] > password.length) {
      return {
        errorMessage: `Password must have at least ${passwordPolicies['PasswordLength']} characters`,
        firstPassword: 'true',
      };
    }

    if (passwordPolicies['UpperCase'] > 0 && !/[A-Z]/.test(password)) {
      return {
        errorMessage: 'Password must have at least one uppercase (A-Z)',
        firstPassword: 'true',
      };
    }

    if (passwordPolicies['LowerCase'] > 0 && !/[a-z]/.test(password)) {
      return {
        errorMessage: 'Password must have at least one lowercase (a-z)',
        firstPassword: 'true',
      };
    }

    if (passwordPolicies['Digit'] > 0 && !/[0-9]/.test(password)) {
      return {
        errorMessage: 'Password must have at least one digit (0-9)',
        firstPassword: 'true',
      };
    }

    if (
      passwordPolicies['Symbol'] > 0 &&
      !/[` !@$%^&*()_+\-=[\]{};'"\\|,.<>/?~]/.test(password)
    ) {
      return {
        errorMessage:
          'Password must have at least one symbol (`!@$%^&*()_+-=[]{};\'"\\|,.<>/?~)',
        firstPassword: 'true',
      };
    }

    if (passwordPolicies['BannedCharacters'] > 0 && /[:#]/.test(password)) {
      return {
        errorMessage: 'Password cannot contain ":" or "#" characters',
        firstPassword: 'true',
      };
    }

    if (password !== passwordConfirmation) {
      return {
        errorMessage: 'Passwords do not match.',
        firstPassword: 'false',
      };
    }

    return null;
  }

  React.useEffect(() => {
    loadData();
  }, []);

  return (
    <DocumentTitle title="My Account - Customer Portal">
      <>
        <Page.NavBar variant="custom-children">
          <NavBar />
        </Page.NavBar>
        {!contactProperties && !error ? (
          <FullPageProgress />
        ) : (
          <>
            <Divider className={classes.clearDivider} />
            <div className="account-common-page">
              <Page.Common>
                <Container maxWidth="sm">
                  <Grid
                    container
                    direction="column"
                    spacing={6}
                    alignItems="center"
                  >
                    <Grid item xs={12}>
                      <Typography
                        variant="h6"
                        align="center"
                        className="myaccount-title"
                      >
                        My Account
                      </Typography>
                    </Grid>

                    <Grid
                      container
                      item
                      spacing={3}
                      className="myaccount-content-wrapper"
                    >
                      <Grid item xs={12}>
                        <Button
                          className={`${classes.changePassBtn} password-btn`}
                          variant="outlined"
                          color="inherit"
                          onClick={() => handlePasswordChange()}
                        >
                          Change Password
                        </Button>
                      </Grid>
                      {error ? (
                        <Grid item xs={12}>
                          <Alert align="center">{error}</Alert>
                        </Grid>
                      ) : (
                        contactProperties.map((category) => {
                          return category.properties.map((property) => {
                            return (
                              <Grid
                                container
                                item
                                xs={12}
                                key={property.name}
                                spacing={1}
                                alignItems="center"
                              >
                                <Grid item xs={12} sm={4}>
                                  <Typography
                                    variant="body2"
                                    className="myaccount-label"
                                  >
                                    {property.label}
                                  </Typography>
                                </Grid>
                                <Grid item xs sm>
                                  <TextField
                                    disabled
                                    variant="outlined"
                                    value={property.value}
                                    fullWidth
                                    className="myaccount-text-field"
                                  />
                                </Grid>
                              </Grid>
                            );
                          });
                        })
                      )}
                    </Grid>
                  </Grid>
                </Container>
              </Page.Common>
            </div>
            {passwordDialog && (
              <LocalTheme theme={customTheme}>
                <Form.Dialog
                  className="change-password-dialog"
                  onCancel={() => setPasswordDialog(false)}
                  submitAlign="right"
                  submitLabel={'Save'}
                  submittingLabel={'Saving...'}
                  onValidate={(data) => {
                    const errors = {};
                    if (data.newPassword && data.confirmPassword) {
                      var error = validatePassword(
                        data.newPassword,
                        data.confirmPassword
                      );
                      if (error != null) {
                        if (error.firstPassword == 'true') {
                          errors.newPassword = error.errorMessage;
                        } else {
                          errors.confirmPassword = error.errorMessage;
                        }
                      }
                    }

                    return errors;
                  }}
                  onSuccess={(data) => {
                    return changePassword(
                      data.oldPassword,
                      data.newPassword
                    ).then(() => setPasswordDialog(false));
                  }}
                  windowProps={{
                    header: 'Change Password',
                    style: { width: 600 },
                  }}
                  renderContent={() => (
                    <React.Fragment>
                      <Form.Fields.Password
                        required
                        name="oldPassword"
                        label="Old Password"
                      />
                      <Form.Fields.Password
                        required
                        name="newPassword"
                        label="New Password"
                      />
                      <Form.Fields.Password
                        required
                        name="confirmPassword"
                        label="Confirm Password"
                      />
                    </React.Fragment>
                  )}
                />
              </LocalTheme>
            )}
          </>
        )}
      </>
    </DocumentTitle>
  );
};

export default MyAccount;
